import { Logger } from '@aiwyn/shared';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, NgZone, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild, inject, signal } from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import type { Core, WebViewerInstance } from '@pdftron/webviewer';
import { NATIVE_CHROME_ELEMENTS } from '../../../../../../shared/components/file-preview/renderers/apryse/apryse-renderer.utils';
import { WebViewerPreloadService } from '../../../../../../shared/services/webviewer/webviewer-preload.service';
import { parseEsignFieldName } from '../../../../utils/esign-field-naming.util';

export interface EsignFieldsProgress {
  completed: number;
  total: number;
}

export interface EsignSubmitPayload {
  xfdfData: string;
  fieldCount: number;
}

export type EsignViewerMode = 'sign' | 'view-only';

/**
 * Owns the Apryse WebViewer instance for the secure-link signing view: acquisition from the
 * shared singleton, per-recipient field locking, the live field counter, and XFDF export on
 * submit. The container owns everything else (hub/signing toggle, HTTP calls, routing).
 */
@Component({
  selector: 'app-esign-signing-viewer',
  standalone: true,
  imports: [CommonModule, MatProgressSpinnerModule],
  templateUrl: './esign-signing-viewer.component.html',
  styleUrl: './esign-signing-viewer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EsignSigningViewerComponent implements OnInit, OnChanges, OnDestroy {
  @Input({ required: true }) originalFileUrl!: string;
  @Input() combinedXfdf: string | null = null;
  @Input({ required: true }) recipientId!: number;
  @Input() filename = 'document.pdf';
  @Input() mode: EsignViewerMode = 'sign';
  /**
   * Any change after the first re-runs the document load. This component outlives a document-load
   * failure (the container keeps it mounted across the hub ↔ signing toggle), so the container
   * bumps this token to give the signer a way out of the error overlay short of a page reload.
   */
  @Input() reloadToken = 0;

  @Output() readonly fieldsChanged = new EventEmitter<EsignFieldsProgress>();
  @Output() readonly submitReady = new EventEmitter<EsignSubmitPayload>();
  @Output() readonly loadError = new EventEmitter<unknown>();
  @Output() readonly exportError = new EventEmitter<unknown>();

  @ViewChild('viewer', { static: true }) private readonly _viewerRef!: ElementRef<HTMLDivElement>;

  readonly isLoading = signal(true);
  readonly hasLoadError = signal(false);

  private _destroyed = false;
  private _loaded = false;
  private _instance: WebViewerInstance | null = null;
  private _instanceReady: Promise<WebViewerInstance | null> | null = null;

  /** Guards field-count recompute against the burst of `annotationChanged` events importAnnotations fires. */
  private _isImporting = false;
  /** The signature-capture listeners are attached to the shared instance once, not per document load. */
  private _signatureCaptureAttached = false;
  private _annotationChangedHandler: (() => void) | null = null;
  private _documentLoadedHandler: (() => void) | null = null;
  private _loadErrorHandler: ((event: unknown) => void) | null = null;
  /** SIGNATURE field currently armed for click-to-sign, between the click and the tool placing an annotation. */
  private _pendingSignatureField: Core.Annotations.Annotation | null = null;

  private readonly _preloadService = inject(WebViewerPreloadService);
  private readonly _ngZone = inject(NgZone);
  private readonly _logger = inject(Logger);

  ngOnInit(): void {
    void this._loadInto(this.originalFileUrl);
  }

  ngOnChanges(changes: SimpleChanges): void {
    const reload = changes['reloadToken'];
    if (reload && !reload.isFirstChange()) {
      this.isLoading.set(true);
      this.hasLoadError.set(false);
      this._loaded = false;
      void this._loadInto(this.originalFileUrl);
    }
  }

  /**
   * Exports this signer's own fields and emits `submitReady`. No-op until the document has loaded.
   * A signed SIGNATURE field is a separate FreeHand/Stamp annotation placed over its (now-blank)
   * FreeText placeholder — both must be included, tagged with the same `esign-field-name`, so the
   * backend's flatten renders the ink and the field-count stays keyed on the FreeText fields only.
   */
  requestSubmit(): void {
    const instance = this._instance;
    if (!instance || !this._loaded) {
      return;
    }

    const fieldName = (a: Core.Annotations.Annotation) => a.getCustomData('esign-field-name');
    const signerFieldNames = new Set(this._getSignerFields(instance).map(fieldName));
    const allSignerAnnotations = instance.Core.annotationManager
      .getAnnotationsList()
      .filter(a => signerFieldNames.has(fieldName(a)));

    instance.Core.annotationManager.exportAnnotations({ annotationList: allSignerAnnotations })
      .then(xfdfData => this.submitReady.emit({ xfdfData, fieldCount: this._getSignerFields(instance).length }))
      .catch(err => {
        this._logger.error('EsignSigningViewerComponent :: exportAnnotations failed', err);
        this.exportError.emit(err);
      });
  }

  private async _loadInto(url: string): Promise<void> {
    const instance = await this._ensureInstance();
    if (this._destroyed) {
      return;
    }
    if (!instance) {
      this.isLoading.set(false);
      this.hasLoadError.set(true);
      this.loadError.emit(new Error('WebViewer instance unavailable'));
      return;
    }

    this._attachDocumentEvents(instance);

    try {
      await instance.Core.documentViewer.closeDocument();
    } catch {
      // closeDocument throws when no document is loaded yet — expected on first load
    }

    // Async load failures surface via the LOAD_ERROR listener above, not a rejection here — this
    // try/catch only guards the rare synchronous throw (e.g. invalid arguments).
    try {
      instance.UI.loadDocument(url, { extension: 'pdf', filename: this.filename });
    } catch (err) {
      this._logger.error('EsignSigningViewerComponent :: loadDocument failed', err);
      this.isLoading.set(false);
      this.hasLoadError.set(true);
      this.loadError.emit(err);
    }
  }

  private async _ensureInstance(): Promise<WebViewerInstance | null> {
    if (this._instance) {
      return this._instance;
    }
    if (!this._instanceReady) {
      this._instanceReady = this._acquireAndMount();
    }
    return this._instanceReady;
  }

  private async _acquireAndMount(): Promise<WebViewerInstance | null> {
    this._preloadService.preload();
    const preloaded = await this._preloadService.acquire();
    if (!preloaded) {
      this._logger.error('EsignSigningViewerComponent :: acquire() returned no instance');
      return null;
    }
    if (this._destroyed) {
      // Destroyed mid-acquire: hand the instance back so it stays reusable, and skip
      // confirmAcquisition() — otherwise the singleton is orphaned and every later preview breaks.
      this._preloadService.release(preloaded.instance, preloaded.container);
      preloaded.container.remove();
      return null;
    }

    const viewerEl = this._viewerRef.nativeElement;
    while (preloaded.container.firstChild) {
      viewerEl.appendChild(preloaded.container.firstChild);
    }
    preloaded.container.remove();
    this._preloadService.confirmAcquisition();

    this._resetSharedInstance(preloaded.instance);

    this._instance = preloaded.instance;
    return preloaded.instance;
  }

  /**
   * The recycled WebViewer singleton is shared with file preview and firm-side placement — reset
   * the chrome/tool-mode/permission state this flow needs rather than trusting what the last
   * consumer left behind.
   */
  private _resetSharedInstance(instance: WebViewerInstance): void {
    instance.UI.enableElements(NATIVE_CHROME_ELEMENTS);
    instance.UI.enableFadePageNavigationComponent();
    instance.UI.enableFeatures([instance.UI.Feature.Print, instance.UI.Feature.Download]);

    if (this.mode === 'view-only') {
      instance.UI.enableViewOnlyMode();
      return;
    }

    instance.UI.disableViewOnlyMode();
    instance.UI.setToolMode('AnnotationEdit');
    this._setPermissionCheckCallback(instance);
  }

  private _setPermissionCheckCallback(instance: WebViewerInstance): void {
    instance.Core.annotationManager.setPermissionCheckCallback((_author: string, annotation: Core.Annotations.Annotation) => {
      const fieldName = annotation.getCustomData('esign-field-name');
      if (!fieldName) {
        return false;
      }
      const parsed = parseEsignFieldName(fieldName);
      return parsed?.recipientId === this.recipientId;
    });
  }

  private _attachDocumentEvents(instance: WebViewerInstance): void {
    this._detachDocumentEvents(instance);

    this._documentLoadedHandler = () => {
      instance.UI.setFitMode(instance.UI.FitMode.FitWidth);
      this._loaded = true;
      this.isLoading.set(false);
      this.hasLoadError.set(false);
      this._restoreCombinedXfdf(instance);
    };
    this._loadErrorHandler = (event: unknown) => {
      const err = event instanceof CustomEvent ? event.detail : event;
      this._logger.error('EsignSigningViewerComponent :: document load error', err);
      this.isLoading.set(false);
      this.hasLoadError.set(true);
      this.loadError.emit(err);
    };

    instance.Core.documentViewer.addEventListener('documentLoaded', this._documentLoadedHandler);
    instance.UI.addEventListener(instance.UI.Events.LOAD_ERROR, this._loadErrorHandler);
  }

  private _detachDocumentEvents(instance: WebViewerInstance): void {
    if (this._documentLoadedHandler) {
      instance.Core.documentViewer.removeEventListener('documentLoaded', this._documentLoadedHandler);
    }
    if (this._loadErrorHandler) {
      instance.UI.removeEventListener(instance.UI.Events.LOAD_ERROR, this._loadErrorHandler);
    }
    if (this._annotationChangedHandler) {
      instance.Core.annotationManager.removeEventListener('annotationChanged', this._annotationChangedHandler);
    }
  }

  /** Imports placement + this signer's own prior values as the starting annotation set. */
  private _restoreCombinedXfdf(instance: WebViewerInstance): void {
    if (this.mode !== 'sign') {
      if (this.combinedXfdf) {
        void instance.Core.annotationManager.importAnnotations(this.combinedXfdf);
      }
      return;
    }

    // Attach the listener before starting the import (not inside its `.then()`) so it is already
    // in place — and sees `_isImporting === true` — for any `annotationChanged` events the import
    // itself fires while in flight.
    this._listenForAnnotationChanges(instance);

    this._isImporting = true;
    const importDone = this.combinedXfdf
      ? instance.Core.annotationManager.importAnnotations(this.combinedXfdf)
      : Promise.resolve();

    importDone.then(() => {
      this._removeOtherSignerFields(instance);
      this._isImporting = false;
      this._prepareSignerFieldsForEditing(instance);
      this._emitFieldsChanged(instance);
    });
  }

  /**
   * A co-signer's fields are theirs to fill and this signer can never edit them — the permission
   * callback already refuses — so leaving them on the page only offers boxes that quietly reject
   * every click. Removed from this viewer alone: the signed PDF is assembled server-side from the
   * stored placement, so every signer's fields still appear in the finished document.
   *
   * Forced, because the same permission callback that makes them unusable would also refuse the
   * delete.
   */
  private _removeOtherSignerFields(instance: WebViewerInstance): void {
    const { annotationManager } = instance.Core;
    const foreign = annotationManager.getAnnotationsList().filter(annotation => {
      const parsed = parseEsignFieldName(annotation.getCustomData('esign-field-name'));
      return parsed != null && parsed.recipientId !== this.recipientId;
    });

    if (foreign.length > 0) {
      annotationManager.deleteAnnotations(foreign, { force: true });
    }
  }

  /**
   * `Locked` blocks moving/resizing/deleting a field regardless of type — placement (firm-portal)
   * never sets it, since staff needs to reposition fields there.
   *
   * The two field types diverge from that shared starting point:
   * - DATE_SIGNED is auto-filled with today's date and stays `LockedContents = true` — the signer
   *   never types a date by hand.
   * - SIGNATURE stays `LockedContents = true` too (never becomes a plain text box); its placeholder
   *   is cleared to blank and the field is instead driven entirely by `_setupSignatureCapture`,
   *   which opens Apryse's native signature modal on click and places the ink/typed signature as
   *   its own annotation over this (now-empty) placeholder.
   */
  private _prepareSignerFieldsForEditing(instance: WebViewerInstance): void {
    const { annotationManager } = instance.Core;
    annotationManager.deselectAllAnnotations();

    this._getSignerFields(instance).forEach(annotation => {
      const parsed = parseEsignFieldName(annotation.getCustomData('esign-field-name'));
      annotation.Locked = true;
      annotation.LockedContents = true;

      const freeText = annotation as Core.Annotations.FreeTextAnnotation;
      if (parsed?.fieldType === 'DATE_SIGNED') {
        freeText.setContents(new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }));
      } else {
        freeText.setContents('');
      }

      annotationManager.redrawAnnotation(annotation);
    });

    this._setupSignatureCapture(instance);
  }

  /**
   * Drives click-to-sign for this signer's SIGNATURE fields via Apryse's native signature tool,
   * since these fields are plain FreeText (no AcroForm widget to hand to `signSignatureWidget`):
   * (A) selecting a SIGNATURE field arms the tool at that field's rect and opens the modal;
   * (B) the tool's placed FreeHand/Stamp annotation is tagged with the same field name and locked
   *     in place, so export and the completion counter can find it; (C) the tool resets to
   *     AnnotationEdit once the modal closes, mirroring the reset in engagements'
   *     `AbstractApryseStep.onInstanceInitialization`.
   */
  private _setupSignatureCapture(instance: WebViewerInstance): void {
    // These listeners live on the shared instance for the component's whole lifetime, so a second
    // documentLoaded (a reload after a failed load) must not stack a second copy of each.
    if (this._signatureCaptureAttached) {
      return;
    }
    this._signatureCaptureAttached = true;

    const { annotationManager, documentViewer } = instance.Core;
    const sigTool = documentViewer.getTool('AnnotationCreateSignature') as Core.Tools.SignatureCreateTool;

    // Keep the signature as a standalone annotation (not baked into a widget appearance) so it
    // survives exportAnnotations() and the backend flatten — mirrors the engagements fix.
    // SigningModes is a static enum not exposed on the instance type — duck-typed like
    // AbstractApryseStep.onInstanceInitialization does for the same lookup.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const SigningModes = (sigTool as any).SigningModes ?? (instance.Core.Tools as any).SignatureCreateTool?.SigningModes;
    if (SigningModes?.ANNOTATION !== undefined && typeof sigTool.setSigningMode === 'function') {
      sigTool.setSigningMode(SigningModes.ANNOTATION);
    }

    this._ngZone.runOutsideAngular(() => {
      // (A) Click a SIGNATURE field → arm the tool at that field's rect and open the modal.
      annotationManager.addEventListener('annotationSelected', (annotations: Core.Annotations.Annotation[], action: string) => {
        if (action !== 'selected') {
          return;
        }

        const target = annotations.find(a => {
          const parsed = parseEsignFieldName(a.getCustomData('esign-field-name'));
          return parsed?.recipientId === this.recipientId && parsed?.fieldType === 'SIGNATURE';
        });
        if (!target) {
          return;
        }

        annotationManager.deselectAllAnnotations(); // never enter inline text-edit
        this._pendingSignatureField = target;

        // NOT a typed/documented API (no `pageCoordinates` setter on SignatureCreateTool in the
        // installed version's type defs) — unverified until confirmed manually in-browser. If the
        // signature doesn't land on the field's rect, fall back to converting SIGNATURE placeholders
        // into SignatureWidgetAnnotation + instance.UI.signSignatureWidget(), the proven path
        // engagements/deliverables already ship.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (sigTool as any).pageCoordinates = [
          { pageNumber: target.PageNumber, x: target.X, y: target.Y },
          { pageNumber: target.PageNumber, x: target.X + target.Width, y: target.Y + target.Height },
        ];
        instance.UI.setToolMode('AnnotationCreateSignature');
        instance.UI.openElements(['signatureModal']);
      });

      // (B) The placed signature arrives as an annotationChanged/'add' (FreeHand or Stamp) — tag it
      // with the field name so export + the completion counter pick it up, and lock it in place.
      annotationManager.addEventListener('annotationChanged', (annotations: Core.Annotations.Annotation[], action: string) => {
        if (action !== 'add' || !this._pendingSignatureField) {
          return;
        }

        const sig = annotations.find(a =>
          a instanceof instance.Core.Annotations.FreeHandAnnotation || a instanceof instance.Core.Annotations.StampAnnotation);
        if (!sig) {
          return;
        }

        const target = this._pendingSignatureField;
        this._pendingSignatureField = null;
        const fieldName = target.getCustomData('esign-field-name');

        // Re-signing replaces, rather than stacking another annotation over the same rect. Every
        // attempt used to survive: the export carried them all, the completion counter could not
        // tell (it only asks whether any ink is tagged with the field), and the flattened PDF drew
        // them overlapping. Clearing first is also what makes a signature editable at all.
        const superseded = annotationManager.getAnnotationsList().filter(annotation =>
          annotation !== sig &&
          annotation.getCustomData('esign-field-name') === fieldName &&
          (annotation instanceof instance.Core.Annotations.FreeHandAnnotation ||
            annotation instanceof instance.Core.Annotations.StampAnnotation));
        if (superseded.length > 0) {
          annotationManager.deleteAnnotations(superseded, { force: true });
        }

        sig.setCustomData('esign-field-name', fieldName);
        sig.NoMove = true;
        sig.NoResize = true;
        sig.Locked = true;
        annotationManager.redrawAnnotation(sig);

        this._ngZone.run(() => this._emitFieldsChanged(instance));
      });

      // (C) Reset tool + clear pending/location when the modal closes (dismiss or after placement).
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      instance.UI.addEventListener(instance.UI.Events.VISIBILITY_CHANGED, (event: any) => {
        const { element, isVisible } = event?.detail ?? event ?? {};
        if (element !== 'signatureModal' || isVisible) {
          return;
        }

        setTimeout(() => {
          if (documentViewer.getToolMode() === sigTool) {
            instance.UI.setToolMode('AnnotationEdit');
          }
          this._pendingSignatureField = null;
          sigTool.clearLocation?.();
        }, 300);
      });
    });
  }

  private _listenForAnnotationChanges(instance: WebViewerInstance): void {
    const { annotationManager } = instance.Core;

    // WHY runOutsideAngular: annotationChanged fires at high frequency while typing into a
    // FreeText field. Only the emitted output re-enters the zone for its own change detection.
    this._ngZone.runOutsideAngular(() => {
      this._annotationChangedHandler = () => {
        if (this._isImporting) {
          return;
        }
        this._ngZone.run(() => this._emitFieldsChanged(instance));
      };
      annotationManager.addEventListener('annotationChanged', this._annotationChangedHandler);
    });
  }

  private _getSignerFields(instance: WebViewerInstance): Core.Annotations.Annotation[] {
    return instance.Core.annotationManager.getAnnotationsList().filter((annotation: Core.Annotations.Annotation) => {
      // A placed signature is tagged with its field's name (see `_setupSignatureCapture`), but it is
      // the *value* of a SIGNATURE field, not a field of its own — counting it would inflate both
      // the completion total and the `fieldCount` sent on submit the moment the signer signs.
      if (annotation instanceof instance.Core.Annotations.FreeHandAnnotation || annotation instanceof instance.Core.Annotations.StampAnnotation) {
        return false;
      }
      const parsed = parseEsignFieldName(annotation.getCustomData('esign-field-name'));
      return parsed?.recipientId === this.recipientId;
    });
  }

  /**
   * A SIGNATURE field's FreeText placeholder stays blank even once signed (the ink/stamp is a
   * separate annotation placed over it) — so completion for SIGNATURE is keyed on whether a
   * FreeHand/Stamp annotation tagged with the same field name exists, not on the FreeText content.
   */
  private _emitFieldsChanged(instance: WebViewerInstance): void {
    const signerFields = this._getSignerFields(instance);
    const fieldName = (a: Core.Annotations.Annotation) => a.getCustomData('esign-field-name');

    const signedFieldNames = new Set(
      instance.Core.annotationManager
        .getAnnotationsList()
        .filter(a => (a instanceof instance.Core.Annotations.FreeHandAnnotation || a instanceof instance.Core.Annotations.StampAnnotation) && !!fieldName(a))
        .map(fieldName),
    );

    const completed = signerFields.filter(annotation => {
      const parsed = parseEsignFieldName(fieldName(annotation));
      if (parsed?.fieldType === 'SIGNATURE') {
        return signedFieldNames.has(fieldName(annotation));
      }
      const content = (annotation as Core.Annotations.FreeTextAnnotation).getContents();
      return content != null && content.trim().length > 0;
    }).length;

    this.fieldsChanged.emit({ completed, total: signerFields.length });
  }

  ngOnDestroy(): void {
    this._destroyed = true;
    if (!this._instance) {
      return;
    }

    const instance = this._instance;
    this._detachDocumentEvents(instance);

    try {
      instance.Core.documentViewer.closeDocument();
    } catch {
      // ignore — closeDocument throws if there is no active doc
    }

    this._restoreSharedInstanceState(instance);
    this._preloadService.release(instance, this._viewerRef.nativeElement);
    this._instance = null;
    this._instanceReady = null;
  }

  /**
   * Clears the permission callback and view-only lock before releasing — otherwise the next
   * consumer of the shared singleton (file preview, firm-side placement) silently inherits this
   * signer's field lock and can no longer edit their own annotations.
   */
  private _restoreSharedInstanceState(instance: WebViewerInstance): void {
    try {
      instance.Core.annotationManager.setPermissionCheckCallback(() => true);
      instance.UI.disableViewOnlyMode();
    } catch (err) {
      this._logger.warn('EsignSigningViewerComponent :: failed to restore shared WebViewer state on release', err);
    }
  }
}
